/**
 * Unit invitations management
 */

import { supabase } from '../supabase'
import type { Role } from '../types'
import { getAuthUser } from './helpers'
import { validateCapability } from '../services/permissions'

/**
 * Invite a user to a unit
 */
export async function inviteUserToUnit(
  unitId: string,
  email: string,
  role: Role,
  userRole?: Role
): Promise<void> {
  if (userRole) {
    await validateCapability(userRole, 'canManageTeam')
  }
  const user = await getAuthUser()
  if (!user) throw new Error('User not authenticated')

  console.log('[inviteUserToUnit] Inviting:', { unitId, email, role })

  // Use RPC function for validation and duplicate checking
  const { data, error } = await supabase
    .rpc('invite_user_to_unit', {
      p_unit_id: unitId,
      p_email: email.toLowerCase().trim(),
      p_role: role
    })

  if (error) {
    console.error('[inviteUserToUnit] Error:', error)
    throw new Error(error.message || 'Erro ao enviar convite')
  }

  console.log('[inviteUserToUnit] Invitation created:', data)
}

/**
 * Get invitations for a unit (RPC preferred, fallback direct query)
 */
export async function getUnitInvitations(unitId: string): Promise<any[]> {
  const { data: rpcData, error: rpcError } = await supabase
    .rpc('get_unit_invitations', { p_unit_id: unitId })

  if (!rpcError && Array.isArray(rpcData)) return rpcData

  if (rpcError) console.warn('[getUnitInvitations] RPC failed, using fallback:', rpcError)

  const { data, error } = await supabase
    .from('unit_invitations')
    .select('*')
    .eq('unit_id', unitId)
    .order('created_at', { ascending: false })

  if (error) throw error
  return data || []
}

/**
 * Cancel/Delete an invitation
 */
export async function cancelInvitation(invitationId: string, userRole?: Role): Promise<void> {
  if (userRole) {
    await validateCapability(userRole, 'canManageTeam')
  }
  console.log('[cancelInvitation] Canceling invitation:', invitationId)
  
  // Use RPC function for better permission handling
  const { error } = await supabase
    .rpc('cancel_unit_invitation', { invitation_id: invitationId })

  if (error) {
    console.error('[cancelInvitation] Error:', error)
    throw new Error(error.message || 'Erro ao cancelar convite')
  }

  console.log('[cancelInvitation] Invitation canceled successfully')
}

/**
 * Get pending invitations for the current user (by email)
 */
export async function getUserInvitations(): Promise<any[]> {
  const user = await getAuthUser()
  if (!user?.email) {
    console.log('[getUserInvitations] No user email')
    return []
  }

  console.log('[getUserInvitations] Fetching for email:', user.email)

  // Use the RPC that already JOINs units + inviter profile in one query
  const { data, error } = await supabase
    .rpc('get_user_invitations_with_units')

  console.log('[getUserInvitations] RPC result:', {
    found: data?.length || 0,
    error: error?.message || 'none',
    errorCode: error?.code || 'none'
  })

  if (error) {
    console.warn('[getUserInvitations] RPC error details:', {
      message: error.message,
      code: error.code,
      details: error.details,
      status: (error as any).status
    })
    return []
  }

  return data || []
}

/**
 * Accept an invitation (RPC expected)
 */
export async function acceptInvitation(invitationId: string): Promise<void> {
  const { error } = await supabase
    .rpc('accept_unit_invitation', { invitation_id: invitationId })

  if (error) throw error
}

/**
 * Decline an invitation
 */
export async function declineInvitation(invitationId: string): Promise<void> {
  const { error } = await supabase
    .from('unit_invitations')
    .update({ status: 'declined' })
    .eq('id', invitationId)

  if (error) throw error
}
